import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  planningApi,
  type CreatePlanningActivityData,
  type DuplicatePlanningItemData,
  type CreatePlanningItemData,
  type CreatePlanningWorkGroupData,
  type MovePlanningItemData,
  type SubmitPlanningWorklogData,
  type UpdatePlanningActivityData,
  type UpdatePlanningDescriptionData,
  type UpdatePlanningItemData,
  type UpdatePlanningWeekLiveData,
  type UpdatePlanningWeekNoteData,
  type UpdatePlanningWorkGroupData,
} from '@/lib/api';
import type {
  PlanningActivity,
  PlanningProductionOverview,
  PlanningWeek,
  PlanningWeekListParams,
  PlanningWorkGroup,
} from '@/features/planning/types';

const planningKeys = {
  all: ['planning'] as const,
  weeks: (params?: PlanningWeekListParams) =>
    [
      'planning',
      'weeks',
      params?.limit ?? null,
      params?.cursor ?? null,
      params?.includeArchived ?? null,
      params?.archivedOnly ?? null,
    ] as const,
  week: (weekId?: string) => ['planning', 'week', weekId] as const,
  production: (weekId?: string) => ['planning', 'production', weekId] as const,
  workGroupsAll: ['planning', 'work-groups'] as const,
  workGroups: (includeInactive?: boolean) => ['planning', 'work-groups', includeInactive] as const,
  activitiesAll: ['planning', 'activities'] as const,
  activities: (includeInactive?: boolean) => ['planning', 'activities', includeInactive] as const,
};

function invalidatePlanningWeek(
  queryClient: ReturnType<typeof useQueryClient>,
  weekId: string | undefined,
) {
  if (!weekId) {
    void queryClient.invalidateQueries({ queryKey: ['planning', 'week'] });
    return;
  }
  void queryClient.invalidateQueries({ queryKey: planningKeys.week(weekId) });
}

function invalidatePlanningWeeksList(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['planning', 'weeks'] });
}

export function usePlanningWeeks(params?: PlanningWeekListParams) {
  return useQuery({
    queryKey: planningKeys.weeks(params),
    queryFn: async () => {
      const response = await planningApi.getWeeks(params);
      return response.data as PlanningWeek[];
    },
  });
}

export function usePlanningWeek(weekId: string | undefined) {
  return useQuery({
    queryKey: planningKeys.week(weekId),
    queryFn: async () => {
      if (!weekId) throw new Error('Week ID is required');
      const response = await planningApi.getWeek(weekId);
      return response.data as PlanningWeek;
    },
    enabled: !!weekId,
  });
}

export function useGeneratePlanningWeek() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (startDate: string) => {
      const response = await planningApi.generateWeek(startDate);
      return response.data as PlanningWeek;
    },
    onSuccess: (generatedWeek) => {
      invalidatePlanningWeeksList(queryClient);
      if (generatedWeek?.id) {
        queryClient.setQueryData(planningKeys.week(generatedWeek.id), generatedWeek);
      }
    },
  });
}

export function useArchivePlanningWeek() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (weekId: string) => {
      const response = await planningApi.archiveWeek(weekId);
      return response.data as PlanningWeek;
    },
    onSuccess: (updatedWeek) => {
      invalidatePlanningWeeksList(queryClient);
      invalidatePlanningWeek(queryClient, updatedWeek?.id);
    },
  });
}

export function useUnarchivePlanningWeek() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (weekId: string) => {
      const response = await planningApi.unarchiveWeek(weekId);
      return response.data as PlanningWeek;
    },
    onSuccess: (updatedWeek) => {
      invalidatePlanningWeeksList(queryClient);
      invalidatePlanningWeek(queryClient, updatedWeek?.id);
    },
  });
}

export function useDeletePlanningWeek() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (weekId: string) => {
      await planningApi.deleteWeek(weekId);
      return weekId;
    },
    onSuccess: (weekId) => {
      invalidatePlanningWeeksList(queryClient);
      void queryClient.removeQueries({ queryKey: planningKeys.week(weekId) });
    },
  });
}

export function useUpdatePlanningWeekNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ weekId, data }: { weekId: string; data: UpdatePlanningWeekNoteData }) => {
      const response = await planningApi.updateWeekNote(weekId, data);
      return response.data as PlanningWeek;
    },
    onSuccess: (updatedWeek, variables) => {
      invalidatePlanningWeek(queryClient, variables.weekId);
      queryClient.setQueryData(planningKeys.week(variables.weekId), updatedWeek);
      invalidatePlanningWeeksList(queryClient);
    },
  });
}

export function useUpdatePlanningWeekLiveStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ weekId, data }: { weekId: string; data: UpdatePlanningWeekLiveData }) => {
      const response = await planningApi.updateWeekLiveStatus(weekId, data);
      return response.data as PlanningWeek;
    },
    onSuccess: (updatedWeek, variables) => {
      invalidatePlanningWeek(queryClient, variables.weekId);
      queryClient.setQueryData(planningKeys.week(variables.weekId), updatedWeek);
      invalidatePlanningWeeksList(queryClient);
    },
  });
}

export function useCreatePlanningItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreatePlanningItemData) => {
      const response = await planningApi.createItem(data);
      return response.data;
    },
    onSuccess: (_createdItem, variables) => {
      invalidatePlanningWeek(queryClient, variables.weekId);
      invalidatePlanningWeeksList(queryClient);
    },
  });
}

export function useUpdatePlanningItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      weekId: string;
      data: UpdatePlanningItemData;
    }) => {
      const response = await planningApi.updateItem(id, data);
      return response.data;
    },
    onSuccess: (_updatedItem, variables) => {
      invalidatePlanningWeek(queryClient, variables.weekId);
    },
  });
}

export function useDeletePlanningItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: string; weekId: string }) => {
      await planningApi.deleteItem(id);
    },
    onSuccess: (_deletedItem, variables) => {
      invalidatePlanningWeek(queryClient, variables.weekId);
      invalidatePlanningWeeksList(queryClient);
    },
  });
}

export function useDuplicatePlanningItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      id: string;
      weekId: string;
      data: DuplicatePlanningItemData;
    }) => {
      const response = await planningApi.duplicateItem(vars.id, vars.data);
      return response.data as { createdCount: number; message?: string };
    },
    onSuccess: (_result, variables) => {
      invalidatePlanningWeek(queryClient, variables.weekId);
      invalidatePlanningWeeksList(queryClient);
    },
  });
}

export function useMovePlanningItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      id: string;
      weekId: string;
      data: MovePlanningItemData;
    }) => {
      const response = await planningApi.moveItem(vars.id, vars.data);
      return response.data;
    },
    onSuccess: (_movedItem, variables) => {
      invalidatePlanningWeek(queryClient, variables.weekId);
    },
  });
}

export function useUpdatePlanningDescription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      weekId?: string;
      data: UpdatePlanningDescriptionData;
    }) => {
      const response = await planningApi.updateDescription(id, data);
      return response.data;
    },
    onSuccess: (_updatedItem, variables) => {
      invalidatePlanningWeek(queryClient, variables.weekId);
    },
  });
}

export function useSubmitPlanningWorklog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SubmitPlanningWorklogData & { weekId?: string }) => {
      const response = await planningApi.submitWorklog({
        planningItemId: data.planningItemId,
        mailsProcessed: data.mailsProcessed,
        note: data.note,
      });
      return response.data;
    },
    onSuccess: (_worklog, variables) => {
      invalidatePlanningWeek(queryClient, variables.weekId);
      if (variables.weekId) {
        void queryClient.invalidateQueries({
          queryKey: planningKeys.production(variables.weekId),
        });
      } else {
        void queryClient.invalidateQueries({ queryKey: ['planning', 'production'] });
      }
    },
  });
}

export function usePlanningProduction(weekId: string | undefined, enabled: boolean = true) {
  return useQuery({
    queryKey: planningKeys.production(weekId),
    queryFn: async () => {
      if (!weekId) throw new Error('Week ID is required');
      const response = await planningApi.getProduction(weekId);
      return response.data as PlanningProductionOverview;
    },
    enabled: enabled && !!weekId,
  });
}

// ============================================
// WORK GROUPS CATALOG
// ============================================

export function usePlanningWorkGroups(includeInactive = false) {
  return useQuery({
    queryKey: planningKeys.workGroups(includeInactive),
    queryFn: async () => {
      const response = await planningApi.getWorkGroups(includeInactive);
      return response.data as PlanningWorkGroup[];
    },
  });
}

export function useCreatePlanningWorkGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreatePlanningWorkGroupData) => {
      const response = await planningApi.createWorkGroup(data);
      return response.data as PlanningWorkGroup;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: planningKeys.workGroupsAll });
    },
  });
}

export function useUpdatePlanningWorkGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdatePlanningWorkGroupData }) => {
      const response = await planningApi.updateWorkGroup(id, data);
      return response.data as PlanningWorkGroup;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: planningKeys.workGroupsAll });
    },
  });
}

export function useDeletePlanningWorkGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await planningApi.deleteWorkGroup(id);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: planningKeys.workGroupsAll });
    },
  });
}

// ============================================
// ACTIVITIES CATALOG
// ============================================

export function usePlanningActivities(includeInactive = false) {
  return useQuery({
    queryKey: planningKeys.activities(includeInactive),
    queryFn: async () => {
      const response = await planningApi.getActivities(includeInactive);
      return response.data as PlanningActivity[];
    },
  });
}

export function useCreatePlanningActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreatePlanningActivityData) => {
      const response = await planningApi.createActivity(data);
      return response.data as PlanningActivity;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: planningKeys.activitiesAll });
    },
  });
}

export function useUpdatePlanningActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdatePlanningActivityData }) => {
      const response = await planningApi.updateActivity(id, data);
      return response.data as PlanningActivity;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: planningKeys.activitiesAll });
    },
  });
}

export function useDeletePlanningActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await planningApi.deleteActivity(id);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: planningKeys.activitiesAll });
    },
  });
}
